import { call, put, takeLatest, take, fork } from "redux-saga/effects";
import {
  signInWithPopup,
  GoogleAuthProvider,
  GithubAuthProvider,
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged,
  User as FirebaseUser,
} from "firebase/auth";
import { analytics, auth } from "../../../config/firebase";
import {
  setUser,
  setInitialized,
  googleSignInRequest,
  githubSignInRequest,
  emailSignInRequest,
  emailSignUpRequest,
  passwordResetRequest,
  passwordResetSuccess,
  signOutRequest,
  authError,
  onLoginSuccess,
} from "./authSlice";
import { EventChannel, eventChannel } from "redux-saga";
import { User } from "@stratify/shared";
import { fetchDocumentsStart } from "../../documents/store/documentsSlice";
import { setUserProperties } from "firebase/analytics";
import { INTERNAL_USERS } from "../../../config/internalUsers";
import { startPropertySubscription } from "../../properties/store/propertiesSlice";

// Convert Firebase user to our User type
async function mapFirebaseUser(
  firebaseUser: FirebaseUser | null
): Promise<User | null> {
  if (!firebaseUser) {
    return null;
  }

  const tokenResult = await firebaseUser.getIdTokenResult();

  return {
    id: firebaseUser.uid,
    email: firebaseUser.email || "",
    displayName: firebaseUser.displayName || undefined,
    photoURL: firebaseUser.photoURL || undefined,
    token: tokenResult.token,
    isBetaUser: tokenResult.claims?.betaUser === true,
  };
}

// Create auth state change channel
function createAuthChannel() {
  return eventChannel((emit) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        emit({ user });
      },
      (error) => {
        emit({ error });
      }
    );

    return unsubscribe;
  });
}

// Watch auth state changes
function* watchAuthStateChanged(): Generator {
  const channel: EventChannel<{ user: FirebaseUser | null; error?: Error }> =
    yield call(createAuthChannel);

  try {
    while (true) {
      const { user, error } = yield take(channel);
      if (error) {
        yield put(authError(error.message));
      } else {
        const userObj = yield mapFirebaseUser(user);
        yield put(setUser(userObj));
        if (userObj) {
          yield put(onLoginSuccess());
        }
        yield put(setInitialized());
      }
    }
  } finally {
    channel.close();
  }
}

// Email sign in
function* signInWithEmail(
  action: ReturnType<typeof emailSignInRequest>
): Generator {
  try {
    const { email, password } = action.payload;
    const result = yield call(
      signInWithEmailAndPassword,
      auth,
      email,
      password
    );
    const userObj = yield mapFirebaseUser(result.user);
    yield put(setUser(userObj));
    yield put(onLoginSuccess());
  } catch (error) {
    yield put(authError((error as Error).message));
  }
}

// Email sign up
function* signUpWithEmail(
  action: ReturnType<typeof emailSignUpRequest>
): Generator {
  try {
    const { email, password, displayName } = action.payload;
    const result = yield call(
      createUserWithEmailAndPassword,
      auth,
      email,
      password
    );

    // Update profile with display name
    yield call(updateProfile, result.user, { displayName });
    const userObj = yield mapFirebaseUser(result.user);
    yield put(setUser(userObj));
    yield put(onLoginSuccess());
  } catch (error) {
    yield put(authError((error as Error).message));
  }
}

// Google sign in
function* signInWithGoogle(): Generator {
  try {
    const provider = new GoogleAuthProvider();
    const result = yield call(signInWithPopup, auth, provider);
    const userObj = yield mapFirebaseUser(result.user);
    yield put(setUser(userObj));
    yield put(onLoginSuccess());
  } catch (error) {
    yield put(authError((error as Error).message));
  }
}

// GitHub sign in
function* signInWithGithub(): Generator {
  try {
    const provider = new GithubAuthProvider();
    const result = yield call(signInWithPopup, auth, provider);
    const userObj = yield mapFirebaseUser(result.user);
    yield put(setUser(userObj));
    yield put(onLoginSuccess());
  } catch (error) {
    yield put(authError((error as Error).message));
  }
}

// Handle post-login tasks
function* handleLoginSuccess(): Generator {
  try {
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not found");
    }

    // if dev mode or user is an interal user, set user to internal
    const userType =
      import.meta.env.MODE === "development" ||
      INTERNAL_USERS.includes(user.email?.toLowerCase() || "")
        ? "internal"
        : "external";

    console.log("User type:", userType);
    if (analytics) {
      // Set user properties in Firebase Analytics
      yield setUserProperties(analytics, {
        userType,
      });
    }

    yield put(fetchDocumentsStart());
    yield put(startPropertySubscription());
  } catch (error) {
    yield put(authError((error as Error).message));
  }
}

// Password reset
function* resetPassword(
  action: ReturnType<typeof passwordResetRequest>
): Generator {
  try {
    yield call(sendPasswordResetEmail, auth, action.payload);
    yield put(passwordResetSuccess());
  } catch (error) {
    yield put(authError((error as Error).message));
  }
}

// Sign out
function* signOut(): Generator {
  try {
    yield call(firebaseSignOut, auth);
    yield put(setUser(null));
  } catch (error) {
    yield put(authError((error as Error).message));
  }
}

// Root auth saga
export default function* authSaga(): Generator {
  yield takeLatest(emailSignInRequest.type, signInWithEmail);
  yield takeLatest(emailSignUpRequest.type, signUpWithEmail);
  yield takeLatest(googleSignInRequest.type, signInWithGoogle);
  yield takeLatest(githubSignInRequest.type, signInWithGithub);
  yield takeLatest(passwordResetRequest.type, resetPassword);
  yield takeLatest(signOutRequest.type, signOut);
  yield takeLatest(onLoginSuccess.type, handleLoginSuccess);
  yield fork(watchAuthStateChanged);
}
